/**
 * Sentry browser SDK initialisation — T123 client transport.
 *
 * The DSN is a PUBLIC client identifier per Sentry docs (the value is meant to
 * ship in the browser bundle so the SDK can post events to Sentry's ingest API).
 * Treating it as a secret would be cargo-cult; the actual access control sits
 * on the Sentry side, project + rate limits. https://docs.sentry.io/concepts/key-terms/dsn-explainer/
 *
 * Single project (`javascript-react` under org `thefittclub`); server + client
 * events differentiate via the `source` tag (T123 D3). EU residency
 * (`*.ingest.de.sentry.io`) is non-reversible per Sentry — see T123 D2.
 */

import * as Sentry from "@sentry/react";
import { scrubPii } from "~/lib/pii-scrub";
import { PREVIEW_HOST_SUFFIX, SERVING_HOST } from "~/config/hosts";

const SENTRY_DSN =
  "https://514858fc0e4c8e7bf3ff8b040bc633b3@o4511449535217664.ingest.de.sentry.io/4511449550487632";

/**
 * Detect deploy environment from the browser hostname. The server is configured
 * separately via wrangler.toml's per-env SENTRY_ENVIRONMENT vars; the client
 * mirrors that taxonomy heuristically so a single Sentry project routes events
 * into the right environment bucket without server-side injection.
 */
function detectEnvironment(): "development" | "preview" | "production" {
  if (typeof window === "undefined") return "development";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "development";
  if (host === SERVING_HOST) return "production";
  if (host.endsWith(PREVIEW_HOST_SUFFIX)) return "preview";
  // Fall through: a custom production domain would land here once mapped.
  return "production";
}

/**
 * Idempotent client-side Sentry init. Safe to call multiple times (the SDK
 * itself rejects re-init), but the typical caller is `entry.client.tsx` once
 * before `hydrateRoot`.
 *
 * Replays + tracing are disabled (T123 D4) to conserve the 5K errors/month
 * free-tier quota; enable in a future ticket if signal demands it.
 */
export function initSentryClient(): void {
  const environment = detectEnvironment();
  // T771 — localhost never describes a member. In the 30 days to 2026-09-18 the
  // `development` environment was 677 of the 1 373 events Sentry accepted (49 %): a dev
  // server without `sw.js`, Playwright's HeadlessChrome, HMR. Every one of them cost
  // quota and several mailed the founder. No init means no envelope and no network —
  // strictly less than a `beforeSend` discard. The server already does the same with an
  // empty DSN in local dev (wrangler.toml). Preview stays on: the smoke run there has
  // been read as signal.
  if (environment === "development") return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    // T123 R-T123-4 — never let the SDK auto-collect IP / cookies / headers. SDK v11
    // dropped `sendDefaultPii` (ignored, not rejected, at runtime) and its `dataCollection`
    // defaults collect all of them, so every data category is switched off by name;
    // `userInfo: false` is what stops IP inference. `frameContextLines` is source code
    // around a stack frame, not member data, and keeps the SDK default.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      // SDK 11.9 added MCP request/response content, on by default.
      mcp: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      stackFrameVariables: false,
    },
    // T123 D4 — disable Replay + Tracing on free tier.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // T840 — no `release` key on purpose. sentryVitePlugin injects the CF Pages SHA as
    // `window.SENTRY_RELEASE.id` and the SDK reads it as the default release; any
    // explicit key, even `undefined`, is spread over that default and erases it.
    // T123 R-T123-2 — defence-in-depth PII scrub on every outgoing event.
    // RouteErrorBoundary + manual captureException calls scrub the visible
    // message/stack first; this hook catches anything the SDK fills in
    // automatically (e.g. breadcrumbs from form fields).
    beforeSend(event) {
      if (event.message) event.message = scrubPii(event.message).slice(0, 500);
      const values = event.exception?.values;
      if (values) {
        for (const v of values) {
          if (v.value) v.value = scrubPii(v.value).slice(0, 500);
        }
      }
      return event;
    },
  });

  // Default tag on every client event so the Sentry alert rule
  // `tags[source] = "client"` (T123 AC #5b) reliably matches.
  Sentry.setTag("source", "client");
}

/**
 * T841 — marks every later event from this page as coming from the offline document the
 * service worker served under another URL, so a 2Q/2R-style event is attributable. Only
 * the requested PATHNAME goes in the breadcrumb: a search string can carry a token.
 * A no-op where Sentry is not initialised (localhost).
 */
export function tagOfflineFallbackBoot(requestedPathname: string): void {
  Sentry.setTag("tfc.document", "offline-fallback");
  Sentry.addBreadcrumb({
    category: "pwa",
    level: "info",
    message: "offline fallback booted under another URL",
    data: { requested: requestedPathname },
  });
}
